import scoutService from '@/services/scoutService'
import scoutRepo from '@/repos/scoutRepo';

import UtilService from "@/services/utilService.js";

import moment from "moment";

let debug = false;

export default  {
    namespaced: true,
    state: {
        scoutings: [],
        pestConfigurations: null,
        scoutingCropStages: [],

        uiState: {
            networkError: false
        },

        totalUnsyncedScouts: 0,
        syncErrors: []
    },
    getters: {
        getTotalUnsyncedScoutings(state) {
            return state.totalUnsyncedScouts;
        },
        getScoutingByID(state) {
            return (scoutingID) => {
                return state.scoutings.find(s => s.ScoutingID === scoutingID);
            }
        },
        getExistingScoutingsForDateAndLocation: (state) => (date, locationID, createdBy) => {
            return state.scoutings.filter(s => {
                let locationIDMatches = s.LocationID == locationID;
                let dateMatches = UtilService.formatDateYMD(moment(s.Date)) == UtilService.formatDateYMD(moment(date));
                let createdByMatches = s.CreatedBy == createdBy;
                
                return locationIDMatches && dateMatches && createdByMatches && !s.Deleted;
            });
        },
        /**
         * Returns any pest configurations where any of the following are true:
         * - it applies to no plants in particular
         * - it applies to at least the given plant in particular
         * 
         * Returns an array, sorted by name.
         */
        getPestConfigurationsForPlant: (state) => (plantID) => {
            return state.pestConfigurations.filter(pc => {
                let appliesInPlant = !pc.PlantIDs || pc.PlantIDs.length === 0 || pc.PlantIDs.includes(plantID);
                let active = pc.Status;

                return appliesInPlant && active;
            }).sort((a, b) => {
                if(a.Name.toLowerCase() < b.Name.toLowerCase()) {
                    return -1;
                }

                if(a.Name.toLowerCase() > b.Name.toLowerCase()) {
                    return 1;
                }

                return 0;
            });
        },
        getPestConfigurationsNotForPlant: (state) => (plantID) => {
            return state.pestConfigurations.filter(pc => {
                let notAppliesInPlant = pc.PlantIDs && pc.PlantIDs.length > 0 && !pc.PlantIDs.includes(plantID);
                let active = pc.Status;

                return notAppliesInPlant && active;
            }).sort((a, b) => {
                if(a.Name.toLowerCase() < b.Name.toLowerCase()) {
                    return -1;
                }

                if(a.Name.toLowerCase() > b.Name.toLowerCase()) {
                    return 1;
                }

                return 0;
            });
        },
        getAllActivePestConfigurations: (state) => {
            return state.pestConfigurations.filter(pc => pc.Status).sort((a, b) => {
                if(a.Name.toLowerCase() < b.Name.toLowerCase()) {
                    return -1;
                }
                if(a.Name.toLowerCase() > b.Name.toLowerCase()) {
                    return 1;
                }
                return 0;
            });
        },
        getScoutings(state, getters, rootState, rootGetters) {
            return (plantIDOrArray, divisionIDOrArray, locationIDOrArray) => {
                let filteredLocationsByProductCategory = rootGetters["PestAndDiseaseStore/getFilteredLocations"]

                // if we passed in an array, use it, but if we passed in a single item then make it into an array with the item in it
                // the .filter calls are to remove any null elements from the array
                let plantIDs = Array.isArray(plantIDOrArray) ? plantIDOrArray : [plantIDOrArray].filter(e => e);
                let divisionIDs = Array.isArray(divisionIDOrArray) ? divisionIDOrArray : [divisionIDOrArray].filter(e => e);
                let locationIDs = Array.isArray(locationIDOrArray) ? locationIDOrArray : [locationIDOrArray].filter(e => e);

                if(state.scoutings) {
                    return state.scoutings.filter(s=> {
                        var match = !s.Deleted;

                        if(plantIDs && plantIDs.length > 0 && plantIDs.indexOf(s.PlantID) < 0) {
                            match = false;
                        }

                        if(divisionIDs && divisionIDs.length > 0 && divisionIDs.indexOf(s.DivisionID) < 0) {
                            match = false;
                        }

                        if(locationIDs && locationIDs.length > 0 && locationIDs.indexOf(s.LocationID) < 0) {
                            match = false;
                        }
                        
                        if (filteredLocationsByProductCategory && filteredLocationsByProductCategory.length > 0 && !filteredLocationsByProductCategory.some(l => l.LocationID == s.LocationID)) {
                            match = false;
                        }

                        return match;
                    }).map(s=> {
                        var location = filteredLocationsByProductCategory.find(l=> l.LocationID == s.LocationID);
                        s.LocationName = location? location.LocationName : "";

                        if (location) {                        
                            var division = location.Division;
                            s.DivisionName = division? division.DivisionName : "";

                            var plant = division.Plant;
                            s.PlantName = plant? plant.PlantName: "";
                        }
                        
                        return s;
                    
                    });
                } else {
                    return [];
                }
            }
        },
        getScoutingCropStages(state) {
            return state.scoutingCropStages
        },
        getSyncErrors(state) {
            return state.syncErrors;
        },
        getPestConfigurationByPestConfigurationID(state) {
            return (pestConfigurationID) => {
                return state.pestConfigurations.find(pc => pc.PestConfigurationID === pestConfigurationID);
            };
        }
    }, 
    mutations: {
        setNetworkError(state, value){
            state.uiState.networkError = value;
        },
        updateScoutings(state, scoutings) {
            state.scoutings = scoutings;
        },
        updatePestConfigurations(state, pestConfigurations) {
            state.pestConfigurations = pestConfigurations;
        },
        updateScoutingCropStages(state, scoutingCropStages) {
            state.scoutingCropStages = scoutingCropStages;
        },
        updateTotalUnsyncedScouts(state, totalUnsyncedScouts) {
            state.totalUnsyncedScouts = totalUnsyncedScouts;
        },
        updateSyncErrors(state, errors) {
            state.syncErrors = errors;
        }
    },
    actions: {
        async handleOffline({ commit }, options){
            try {
                await options.action();
                commit("setNetworkError", false);
            } catch(e){
                if (debug) console.log("error when attempting an action in handleOffline: ", e);
                commit("setNetworkError", true);
            }
        },

        /* counts the number of unsynced scouting records in the IndexedDB, so that the count shown in the UI can be updated */
        async updateTotalUnsyncedScouts({commit}) {
            let totalUnsyncedScouts = (await scoutService.getUnsyncedScoutings()).length;

            commit("updateTotalUnsyncedScouts", totalUnsyncedScouts);
        },

        async syncUnsyncedData({commit, dispatch}, options) {
            await dispatch("handleOffline", {
                async action(){
                    if (debug) console.log("attempting scoutService.postUnsyncedData()");
                    let errors = await scoutService.postUnsyncedData(options);
                    commit('updateSyncErrors', errors);
                }
            });

            // load from IndexedDB after we have synced unsynced data - whether the sync was successful or not
            let scoutings = await scoutRepo.getScoutings();
            commit('updateScoutings', scoutings);

            await dispatch("updateTotalUnsyncedScouts");
        },

        async loadScoutings({commit, dispatch}, options) {
            // try to load from the server - if we go offline then just continue and try to load from IndexedDB

            await dispatch("handleOffline", {
                async action(){
                    if (debug) console.log("attempting scoutService.fetchAndStoreScoutings()");

                    // if successful, this will store the data in IndexedDB
                    await scoutService.fetchAndStoreScoutings(options);
                }
            });

            // load from IndexedDB
            let scoutings = await scoutRepo.getScoutings();
            commit('updateScoutings', scoutings);

            await dispatch("updateTotalUnsyncedScouts");
        },

        async loadPestConfigurations({commit, dispatch}, options) {
            // try to load from the server - if we go offline then just continue and try to load from IndexedDB

            await dispatch("handleOffline", {
                async action(){
                    if (debug) console.log("attempting scoutService.fetchAndStorePestConfigurations()");

                    // if successful, this will store the data in IndexedDB
                    await scoutService.fetchAndStorePestConfigurations(options);
                }
            });

            // load from IndexedDB
            let pestConfigurations = await scoutRepo.getPestConfigurations();
            commit('updatePestConfigurations', pestConfigurations);
        },

        async loadScoutingCropStages({commit, dispatch}) {
            await dispatch("handleOffline", {
                async action(){
                    if (debug) console.log("attempting scoutService.fetchAndStoreScoutingCropStages()");

                    // if successful, this will store the data in IndexedDB
                    await scoutService.fetchAndStoreScoutingCropStages();
                }
            });

            // load from IndexedDB
            let scoutingCropStages = await scoutRepo.getScoutingCropStages();
            commit('updateScoutingCropStages', scoutingCropStages);
        },

        async saveScout({commit, dispatch}, options) {
            let scouting = options.scouting
            // if this is a newly created item, give it a unique negative number
            if (!scouting.ScoutingID) {
                scouting.ScoutingID = await scoutService.getNextScoutingNegativeID();
            }

            await dispatch("handleOffline", {
                async action() {
                    await scoutService.saveScout(options);
                }
            });

            // reload the scoutings from the database (note that we do not attempt a load to the server here)
            let scoutings = await scoutRepo.getScoutings();
            commit('updateScoutings', scoutings);

            await dispatch("updateTotalUnsyncedScouts");
        }
    }
}

